const normalizeIdentityValue = (value: any) => {
  const normalized = String(value ?? '').trim();
  if (
    normalized.length === 0 ||
    normalized === 'null' ||
    normalized === 'undefined'
  ) {
    return '';
  }
  return normalized;
};

const normalizeComparableText = (value: any) =>
  normalizeIdentityValue(value).toLowerCase();

export const isTrue = (value: any) =>
  value === true || String(value).toLowerCase() === 'true';

export const getProductFileDedupKey = (
  file: any,
  fallbackKey = ''
): string => {
  const fileId = Number(file?.FileId || 0);
  if (fileId > 0) {
    return `id:${fileId}`;
  }

  const filePath = normalizeIdentityValue(file?.FilePath);
  if (filePath) {
    return `path:${filePath}`;
  }

  const s3FilePath = normalizeIdentityValue(file?.S3FilePath);
  if (s3FilePath) {
    return `s3:${s3FilePath}`;
  }

  const heading = normalizeComparableText(file?.Heading);
  const fileName = normalizeComparableText(file?.FileName);
  const fileType = normalizeComparableText(file?.FileType);

  return `meta:${heading}__${fileName}__${fileType}__${fallbackKey}`;
};

export const mergeUniqueProductFiles = (...fileGroups: any[][]) => {
  const uniqueFiles = new Map<string, any>();

  fileGroups.forEach((group, groupIndex) => {
    (group ?? []).forEach((file: any, itemIndex: number) => {
      if (!file) return;

      const key = getProductFileDedupKey(
        file,
        `${groupIndex}-${itemIndex}`
      );
      if (!uniqueFiles.has(key)) {
        uniqueFiles.set(key, file);
      }
    });
  });

  return Array.from(uniqueFiles.values());
};

export const isSameProductFileReference = (candidate: any, target: any) => {
  if (!candidate || !target) return false;

  const candidateId = Number(candidate?.FileId || 0);
  const targetId = Number(target?.FileId || 0);
  if (candidateId > 0 && targetId > 0 && candidateId === targetId) {
    return true;
  }

  const candidateFilePath = normalizeIdentityValue(candidate?.FilePath);
  const targetFilePath = normalizeIdentityValue(target?.FilePath);
  if (
    candidateFilePath &&
    targetFilePath &&
    candidateFilePath === targetFilePath
  ) {
    return true;
  }

  const candidateS3Path = normalizeIdentityValue(candidate?.S3FilePath);
  const targetS3Path = normalizeIdentityValue(target?.S3FilePath);
  if (candidateS3Path && targetS3Path && candidateS3Path === targetS3Path) {
    return true;
  }

  const candidateHeading = normalizeComparableText(candidate?.Heading);
  const targetHeading = normalizeComparableText(target?.Heading);
  const candidateFileName = normalizeComparableText(candidate?.FileName);
  const targetFileName = normalizeComparableText(target?.FileName);
  const candidateFileType = normalizeComparableText(candidate?.FileType);
  const targetFileType = normalizeComparableText(target?.FileType);

  if (
    candidateHeading &&
    targetHeading &&
    candidateHeading === targetHeading &&
    candidateFileName &&
    targetFileName &&
    candidateFileName === targetFileName
  ) {
    return true;
  }

  if (
    candidateHeading &&
    targetHeading &&
    candidateHeading === targetHeading &&
    candidateFileType &&
    targetFileType &&
    candidateFileType === targetFileType &&
    candidateFileName &&
    targetFileName &&
    candidateFileName === targetFileName
  ) {
    return true;
  }

  return false;
};

const isSameProductFileIdentity = (candidate: any, target: any) => {
  if (!candidate || !target) return false;
  if (candidate === target) return true;

  const candidateId = Number(candidate?.FileId || 0);
  const targetId = Number(target?.FileId || 0);
  if (candidateId > 0 && targetId > 0 && candidateId === targetId) {
    return true;
  }

  const candidateFilePath = normalizeIdentityValue(candidate?.FilePath);
  const targetFilePath = normalizeIdentityValue(target?.FilePath);
  if (
    candidateFilePath &&
    targetFilePath &&
    candidateFilePath === targetFilePath
  ) {
    return true;
  }

  const candidateS3Path = normalizeIdentityValue(candidate?.S3FilePath);
  const targetS3Path = normalizeIdentityValue(target?.S3FilePath);
  if (candidateS3Path && targetS3Path && candidateS3Path === targetS3Path) {
    return true;
  }

  return false;
};

export const collectMatchingDeletedFiles = (
  processes: any[] = [],
  targetFile: any,
  options?: { commonOnly?: boolean; tabHeading?: string; exactIdentityOnly?: boolean }
) => {
  const oldDeletedFiles: any[] = [];
  const newDeletedFiles: any[] = [];

  processes.forEach((process: any) => {
    (process?.fileIndices ?? []).forEach((fileIndex: any) => {
      if (isTrue(fileIndex?.IsDimension) || isTrue(fileIndex?.isDimension)) {
        return;
      }

      const fileIndexIsCommon =
        isTrue(fileIndex?.IsDefault) || isTrue(fileIndex?.isDefault);
      if (
        typeof options?.commonOnly === 'boolean' &&
        fileIndexIsCommon !== options.commonOnly
      ) {
        return;
      }

      if (options?.tabHeading) {
        const currentHeading = String(fileIndex?.FileDetail?.[0]?.Heading || '').trim().toLowerCase();
        const targetHeading = String(options.tabHeading).trim().toLowerCase();
        if (currentHeading && targetHeading && currentHeading !== targetHeading) {
          return;
        }
      }

      (fileIndex?.FileDetail ?? []).forEach((file: any) => {
        const isSameFile = options?.exactIdentityOnly
          ? isSameProductFileIdentity(file, targetFile)
          : isSameProductFileReference(file, targetFile);
        if (!isSameFile) return;

        if (file?.IsNew || Number(file?.FileId || 0) <= 0) {
          newDeletedFiles.push(file);
          return;
        }

        oldDeletedFiles.push(file);
      });
    });
  });

  return {
    oldDeletedFiles: mergeUniqueProductFiles(oldDeletedFiles),
    newDeletedFiles: mergeUniqueProductFiles(newDeletedFiles),
  };
};
